/**
 * assets/buy.js — the buy box (sections/buy.liquid): the bundle size, per-flavour
 * quantities, the purchase option and its plan, live prices, add-to-cart, the cart
 * hand-off, and the gallery.
 *
 * Everything the script needs that only Liquid knows — money format, routes, the
 * variants with their prices and selling-plan allocations, the discount rates, the
 * translated strings — is rendered into a JSON block in the section. Nothing here is
 * English and nothing here is a price. The arithmetic is assets/bundle-pricing.js.
 *
 * Behaviour, per the Home page frame 288:9201 and the bundle brief:
 *   - on the LP 2 template the stops are absent and the rows are flavour cards, a radio
 *     group worth one pack of the picked flavour; both ride the same state below, so
 *     nothing else here knows which page it is on
 *   - the bundle-size stops are an indicator: the flavour rows count packs per
 *     variant (− at 0, + at the section's maximum per flavour) and the stop whose size
 *     they add up to lights. A click on a stop sets the rows to its size — from
 *     nothing, all on the first flavour in stock; else added to the first flavour, or
 *     taken from the last ones chosen. Each stop states the discount at that size as a percentage,
 *     for the purchase option that is checked. The last stop is open-ended: its LABEL
 *     reads "48+ cans" until the rows reach it and then takes the real quantity, while
 *     its percentage carries no "+", the tier being capped there
 *   - both purchase cards price the chosen size in their own terms (variant prices vs
 *     the plan's allocation), with the tier discount per pack AND the store's automatic
 *     welcome discount layered in, compare-at struck through and the per-can price; a
 *     stop's percentage is that same stack against compare-at, so the ladder and the
 *     card always tell the same story. Nothing on the card states the recurring
 *     price, by instruction — bundle-pricing.js still computes it as `subtotal`
 *   - the welcome percentage is the live cart's own automatic discount when one matches
 *     the configured title, so a change in the admin discount reprices the page; the
 *     section's figure (shop metafield, else setting) stands until then
 *   - the CTA is "Choose flavors" and disabled while nothing is chosen, then "Add to
 *     cart {{ subtotal }}" in the chosen option's terms
 *   - `?freq=<weeks>` preselects that plan (resolved by interval, never id) and the
 *     subscribe card
 *   - submit removes any earlier bundle lines (those carrying `_bundle_key`) in one
 *     /cart/update.js call — never /cart/clear.js — then adds every flavour in one
 *     /cart/add.js call with `selling_plan` when subscribing and hidden `_bundle_key`
 *     / `_bundle_tier` properties. A failure with a plan attached re-reads
 *     /products/<handle>.js, resolves the plan by interval again and retries once.
 *     Success dispatches `cart:added`, then hands the moment to whoever owns the cart
 *     UI: Recharge's storefront cart (assets/recharge-cart.js) where it is installed,
 *     else a theme drawer listening for the cancelable `cart:open`. The cart PAGE is
 *     never used, by instruction, so with neither present the shopper stays put with
 *     the confirmation and a refreshed header count. No cart UI is built here
 *   - one polite live region carries every announcement: size and price changes and
 *     the add result
 *   - an "Add <flavour>" button anywhere on the page (the flavour panels carry one, as
 *     `[data-add-flavor]`) puts one more pack of that flavour in its row, brings the row
 *     into view and hands it focus; with no such row on the page the button's own link
 *     is left to work
 *   - the gallery: thumbnails swap the image, arrows step it, the tab scrolls the strip.
 *     IT IS NOT TIED TO THE FLAVOUR ROWS. Adding a flavour used to swap the gallery to
 *     that variant's featured media; removed on instruction 2026-09-21. The buy box sells
 *     several flavours at once, so "the variant" is not a thing the gallery can show, and
 *     a shopper adding a third pack had the photograph they were looking at replaced out
 *     from under them. The gallery is the product's own sequence and only its own
 *     controls move it (figma-changes §7.87);
 *     adding a flavour shows that variant's image
 */
import { quote, bundleLines, resolvePlans, planByKey } from 'bundle-pricing';
import { openRechargeCart } from 'recharge-cart';

const PLACEHOLDERS = {
  '{{amount}}': (c) => (c / 100).toFixed(2),
  '{{amount_no_decimals}}': (c) => Math.round(c / 100).toString(),
  '{{amount_with_comma_separator}}': (c) => (c / 100).toFixed(2).replace('.', ','),
  '{{amount_no_decimals_with_comma_separator}}': (c) => Math.round(c / 100).toString(),
  '{{amount_with_apostrophe_separator}}': (c) => (c / 100).toFixed(2),
};

function formatMoney(cents, format) {
  const key = Object.keys(PLACEHOLDERS).find((k) => format.includes(k)) || '{{amount}}';
  let value = PLACEHOLDERS[key](cents);
  // Thousands separators for the formats that use them.
  const [whole, fraction] = value.split(/[.,]/);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, key.includes('comma_separator') ? '.' : key.includes('apostrophe') ? "'" : ',');
  value = fraction !== undefined ? `${grouped}${key.includes('comma_separator') ? ',' : '.'}${fraction}` : grouped;
  return format.replace(key, value).replace(/<[^>]*>/g, '');
}

function fill(template, values) {
  return Object.entries(values).reduce((s, [k, v]) => s.split(k).join(v), template || '');
}

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const uuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

async function json(url, init = {}) {
  const response = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
  });
  if (!response.ok) {
    const error = new Error(String(response.status));
    error.status = response.status;
    try {
      error.body = await response.json();
    } catch {
      /* no body */
    }
    throw error;
  }
  return response.json();
}

function initBuy(root) {
  if (root.dataset.buyReady) return;
  root.dataset.buyReady = 'true';

  const dataNode = root.querySelector('[data-buy-data]');
  if (!dataNode) return;
  const data = JSON.parse(dataNode.textContent);
  const s = data.strings;
  const variants = new Map(data.variants.map((v) => [String(v.id), v]));
  const money = (cents) => formatMoney(cents, data.moneyFormat);

  const rows = [...root.querySelectorAll('[data-buy-row]')];
  const cards = [...root.querySelectorAll('[data-buy-option]')];
  const stops = [...root.querySelectorAll('[data-buy-stop]')];
  const stopsBox = root.querySelector('[data-buy-stops]');
  const form = root.querySelector('[data-buy-form]');
  const submit = root.querySelector('.buy__submit');
  const submitLabel = submit ? submit.querySelector('.button__label') : null;
  const status = root.querySelector('[data-buy-status]');
  const live = root.querySelector('[data-buy-live]');
  const planSelect = root.querySelector('select[name="buy-plan"]');

  const baseVariant = () => [...variants.values()].find((v) => v.available) || [...variants.values()][0] || null;
  // A flavour card's quantity IS its radio — checked and in stock is one pack — and a
  // row's is its count. Read live, because a browser restoring the form on Back does it
  // after this script has run and fires no change event: a cached data-quantity would
  // then show one flavour checked and add another (figma-changes §7.104).
  const quantityOf = (row) => {
    const radio = row.querySelector('input[type="radio"]');
    if (radio) return radio.checked && !radio.disabled ? 1 : 0;
    return Number(row.dataset.quantity) || 0;
  };
  const chosen = () =>
    rows
      .map((row) => ({ row, variant: variants.get(row.dataset.variantId), quantity: quantityOf(row) }))
      .filter((c) => c.variant && c.quantity > 0);
  const totalPacks = () => rows.reduce((n, row) => n + quantityOf(row), 0);
  const largestStop = () => stops.reduce((max, stop) => Math.max(max, Number(stop.dataset.buyStop) || 0), 0) || data.packsMax || 1;
  const maxPerFlavor = () => (Number(data.maxPerFlavor) > 0 ? Number(data.maxPerFlavor) : Infinity);
  const currentOption = () => root.querySelector('.selector-radio .radio-field__input:checked')?.value || 'one-time';
  const currentPlanId = () => (planSelect && planSelect.value ? Number(planSelect.value) : null);
  const currentFrequency = () => planSelect?.selectedOptions[0]?.dataset.frequency || '';
  const optionLabel = (value) => root.querySelector(`[data-buy-option="${value}"] .selector-radio__label`)?.textContent.trim() || value;

  const unitFor = (variant, option, planId) => {
    if (option === 'subscribe' && planId) {
      const plan = variant.plans.find((p) => Number(p.id) === planId);
      if (plan) return plan.price;
    }
    return variant.price;
  };
  // Compare-at is the variant price × packs — the undiscounted pack, not a "was" price.
  const compareFor = (variant) => variant.price;

  const quoteFor = (option, packs) => {
    const planId = currentPlanId();
    return quote({
      lines: bundleLines({
        chosen: chosen(),
        packs,
        base: baseVariant(),
        unitOf: (v) => unitFor(v, option, planId),
        compareOf: compareFor,
      }),
      packs,
      tierBps: data.tierBps,
      welcomeBps: data.welcomeBps,
      cansPerPack: data.cansPerPack,
    });
  };

  // `plus` is the open-ended form ("48+ cans"), used while the rows have not reached the
  // last stop; past it the label is the real size.
  const sizeLabel = (packs, plus = false) => {
    if (data.cansPerPack > 0) return fill(plus ? s.cansPlus : s.cans, { '%c': String(packs * data.cansPerPack) });
    if (plus) return fill(s.packsPlus, { '%n': String(packs) });
    return packs === 1 ? s.packsOne : fill(s.packsOther, { '%n': String(packs) });
  };

  // ---- the one live region ----
  let liveTimer = 0;
  function announce(text, { immediate = false } = {}) {
    if (!live) return;
    clearTimeout(liveTimer);
    const speak = () => {
      live.textContent = '';
      requestAnimationFrame(() => {
        live.textContent = text;
      });
    };
    if (immediate) speak();
    else liveTimer = setTimeout(speak, 400);
  }

  const setText = (scope, selector, text) => {
    const node = scope.querySelector(selector);
    if (node) node.textContent = text;
  };

  // WHERE THE DELIVERY LINE SITS IS A MEASUREMENT, not a breakpoint. It belongs under the
  // label, beside the price's per-can line, where it costs the card no height; but that
  // column is whatever the price leaves, and "144 cans delivered every 4 weeks" does not
  // fit it on a phone. So: try the column, and give the line the card's width only when
  // the string it was just handed does not fit. CSS cannot ask that question and this is
  // the module that writes the string. `nowrap` in the narrow state is what makes
  // scrollWidth answer it. figma-changes §7.97.
  const WIDE = 'selector-radio__description--wide';
  function fitDescription(node) {
    node.classList.remove(WIDE);
    if (node.textContent.trim() && node.scrollWidth > node.clientWidth) node.classList.add(WIDE);
  }
  const fitDescriptions = () => {
    for (const card of cards) {
      const node = card.querySelector('.selector-radio__description');
      if (node) fitDescription(node);
    }
  };

  function update({ speak = false } = {}) {
    const total = totalPacks();
    // Nothing chosen prices as one pack, so the cards always show a figure.
    const packs = Math.max(1, total);
    const complete = total > 0;
    const frequency = currentFrequency();
    const planId = currentPlanId();

    for (const card of cards) {
      const option = card.dataset.buyOption;
      const q = quoteFor(option, packs);

      // One pack's price at this size, welcome discount included — the same figure as
      // the lit stop's — and one pack's compare-at beside it.
      const each = Math.round(q.firstOrder / packs);
      const compareEach = Math.round(q.compare / packs);
      setText(card, '[data-buy-price]', money(each));
      const compareNode = card.querySelector('[data-buy-compare]');
      if (compareNode) {
        compareNode.hidden = !(compareEach > each);
        if (compareEach > each) compareNode.textContent = money(compareEach);
      }
      const description = card.querySelector('.selector-radio__description');
      if (description) {
        // Written first, measured below: the line's own width decides where it sits.
        if (option === 'subscribe') {
          description.textContent =
            q.cans > 0
              ? fill(s.deliveredEvery, { '%c': String(q.cans), '%f': frequency })
              : fill(s.deliveredEveryPlain, { '%f': frequency });
        } else {
          description.textContent = q.cans > 0 ? fill(s.deliveredOnce, { '%c': String(q.cans) }) : '';
        }
        fitDescription(description);
      }
    }

    // The stops: light the one the rows add up to (the last from its size on), and
    // write each saving — the whole stack as a percentage off compare-at, for THE
    // OPTION THAT IS CHECKED. It used to be the subscriber's saving at every size
    // whatever the shopper had picked, which read a third off to someone buying once
    // (figma-changes §7.94). Reading `currentOption()` costs nothing: the radio's
    // change already calls update().
    const base = baseVariant();
    const savingsOption = currentOption();
    const largest = largestStop();
    // How much of the connector is behind the lit stop — see the note on
    // .quantity-steps__row::after. Zero means the first stop or none, and both draw
    // nothing, there being no segment behind the first dot.
    let stopIndex = 0;
    let reachedIndex = 0;
    for (const stop of stops) {
      const stopPacks = Number(stop.dataset.buyStop) || 0;
      const open = stop.hasAttribute('data-buy-stop-open');
      const reached = open ? total >= stopPacks : total === stopPacks;
      const active = total > 0 && reached;
      stop.classList.toggle('quantity-step--active', active);
      if (active) {
        stop.setAttribute('aria-current', 'step');
        reachedIndex = stopIndex;
      } else {
        stop.removeAttribute('aria-current');
      }
      stopIndex += 1;

      // The open-ended stop stands for its own size and every size past it, so once the
      // rows reach it both its label and its saving are the real quantity's.
      const packsShown = open && total > stopPacks ? total : stopPacks;
      const label = stop.querySelector('[data-buy-stop-label]');
      if (label) label.textContent = sizeLabel(packsShown, open && !active);

      const saving = stop.querySelector('[data-buy-tier-saving]');
      if (!saving) continue;
      const q = base
        ? quote({
            lines: [{ unitCents: unitFor(base, savingsOption, planId), compareUnitCents: compareFor(base), packs: packsShown }],
            packs: packsShown,
            tierBps: data.tierBps,
            welcomeBps: data.welcomeBps,
          })
        : null;
      // The price of one pack at this size. No "+" on the open-ended stop's, where its
      // label has one: the tier is capped there, so six packs cost the same each as three.
      const each = q ? Math.round(q.firstOrder / packsShown) : 0;
      saving.hidden = !(each > 0);
      if (each > 0) saving.textContent = fill(s.priceEach, { '%p': money(each) });
    }

    if (stopsBox) {
      stopsBox.style.setProperty('--steps-count', String(stops.length));
      stopsBox.style.setProperty('--steps-reached', String(reachedIndex));
    }

    // Row controls: − at 0, + at the maximum packs of one flavour.
    const cap = maxPerFlavor();
    for (const row of rows) {
      const qty = quantityOf(row);
      const minus = row.querySelector('[data-buy-step="-1"]');
      if (minus) minus.disabled = qty <= 0;
      const plus = row.querySelector('[data-buy-step="1"]');
      if (plus) plus.disabled = qty >= cap;
    }

    if (submit) {
      submit.disabled = !complete;
      if (submitLabel) submitLabel.textContent = complete ? s.addToCart : s.chooseFlavors;
    }

    if (speak) {
      const q = quoteFor(currentOption(), packs);
      announce(fill(s.selection, { '%c': sizeLabel(packs), '%o': optionLabel(currentOption()), '%a': money(q.firstOrder) }));
    }
  }

  function writeQuantity(row, next) {
    const quantity = Math.max(0, next);
    row.dataset.quantity = String(quantity);
    row.classList.toggle('selector-flavor-row--active', quantity > 0);
    const count = row.querySelector('[data-buy-count]');
    if (count) count.textContent = String(quantity);
  }

  // "add" and the stepper swap places and either step disables at an end, so the control
  // under the keyboard can go away; move focus to one that is still usable.
  function keepFocus(row) {
    const active = document.activeElement;
    if (!active || !row.contains(active)) return;
    const usable = (el) => Boolean(el) && el.offsetParent !== null && !el.disabled;
    if (usable(active)) return;
    const step = active.dataset.buyStep;
    const opposite = step === '1' ? '-1' : step === '-1' ? '1' : null;
    const next = [
      row.querySelector('[data-buy-add]'),
      opposite ? row.querySelector(`[data-buy-step="${opposite}"]`) : null,
      row.querySelector('[data-buy-step="1"]'),
      row.querySelector('[data-buy-step="-1"]'),
    ].find(usable);
    next?.focus();
  }

  function setQuantity(row, next) {
    writeQuantity(row, Math.min(maxPerFlavor(), Math.max(0, next)));
    update({ speak: true });
    keepFocus(row);
  }

  for (const row of rows) {
    row.querySelector('[data-buy-add]')?.addEventListener('click', () => {
      setQuantity(row, 1);
    });
    for (const step of row.querySelectorAll('[data-buy-step]')) {
      step.addEventListener('click', () => setQuantity(row, quantityOf(row) + Number(step.dataset.buyStep)));
    }
  }

  // A stop sets the rows to its size (asked for on 2026-10-21: "12 cans" with nothing
  // chosen picks the first flavour). Packs go on the in-stock flavours in row order, each
  // up to its cap, and come off the last chosen first, so what the shopper picked
  // survives where it can. The open-ended stop is reached at its size and left alone past it.
  function pickSize(stop) {
    const target = Number(stop.dataset.buyStop) || 0;
    const total = totalPacks();
    if (!target || total === target || (stop.hasAttribute('data-buy-stop-open') && total > target)) return;
    const cap = maxPerFlavor();
    let delta = target - total;
    const order = delta > 0 ? rows : [...rows].reverse();
    for (const row of order) {
      if (!delta) break;
      const qty = quantityOf(row);
      if (delta > 0) {
        if (!variants.get(row.dataset.variantId)?.available) continue;
        const add = Math.min(delta, cap - qty);
        if (add > 0) writeQuantity(row, qty + add);
        delta -= Math.max(0, add);
      } else {
        const take = Math.min(-delta, qty);
        if (take > 0) writeQuantity(row, qty - take);
        delta += take;
      }
    }
    update({ speak: true });
  }

  for (const stop of stops) {
    stop.querySelector('[data-buy-stop-pick]')?.addEventListener('click', () => pickSize(stop));
  }

  // The card picker (snippets/selector-flavors.liquid, style cards — the LP 2 buy box):
  // ONE flavour, ONE pack. Each card is a row like any other — quantityOf() reads its
  // radio — so everything above prices and adds it as it would the rows. data-quantity
  // is kept in step for anything reading the markup. A page of rows has no radios here
  // and none of this runs.
  const picks = rows.map((row) => row.querySelector('input[type="radio"]')).filter(Boolean);
  function syncPicks() {
    for (const row of rows) {
      const input = row.querySelector('input[type="radio"]');
      if (input) row.dataset.quantity = input.checked && !input.disabled ? '1' : '0';
    }
  }
  if (picks.length) syncPicks();

  root.addEventListener('change', (event) => {
    if (picks.includes(event.target)) {
      syncPicks();
      update({ speak: true });
    } else if (event.target.matches('.selector-radio .radio-field__input') || event.target === planSelect) {
      update({ speak: true });
    }
  });

  // A flavour picked from elsewhere on the page — the header drawer's flavour cards carry
  // [data-pick-flavor] — checks that card here and brings the picker into view, label
  // first, so the shopper lands on what they chose with the price under it. Asked on
  // 2026-10-14 for LP 2, which has no Flavors section for those cards to scroll to.
  // Cards only: with rows (the home page) nothing is claimed and the drawer's link to
  // the flavour panel stands. A sold-out flavour is shown, not picked. The drawer closes
  // itself first: its own listener sits on the panel, below this one on the way up.
  //
  // Both document listeners below outlive their section in the theme editor, which
  // re-renders it and runs initBuy again; a listener whose root is gone steps aside
  // rather than cancelling the click on the live one's behalf.
  document.addEventListener('click', (event) => {
    if (!root.isConnected) return;
    const trigger = event.target.closest('[data-pick-flavor]');
    if (!trigger || event.defaultPrevented || !picks.length) return;
    const row = rows.find((r) => r.dataset.flavor === trigger.dataset.pickFlavor);
    const input = row?.querySelector('input[type="radio"]');
    if (!input) return;
    event.preventDefault();
    if (!input.disabled && !input.checked) {
      input.checked = true;
      syncPicks();
      update({ speak: true });
    }
    const picker = root.querySelector('[data-buy-flavors]') || row;
    picker.scrollIntoView({ block: 'start', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    // Focus follows the shopper to the group. The tapped link is inside a drawer that has
    // just closed, so leaving focus there would drop it to <body>; a sold-out card cannot
    // take it, so the group's checked (or first usable) radio does.
    const target = input.disabled ? picks.find((p) => p.checked) || picks.find((p) => !p.disabled) : input;
    target?.focus({ preventScroll: true });
  });

  // The flavour panels' "Add <flavour>" buttons: one more pack of that flavour, the row
  // brought into view, focus on its +. A flavour this product does not sell, or one sold
  // out, leaves the click alone so the button's own link can act.
  document.addEventListener('click', (event) => {
    if (!root.isConnected) return;
    const trigger = event.target.closest('[data-add-flavor]');
    if (!trigger || event.defaultPrevented) return;
    const row = rows.find((r) => r.dataset.flavor === trigger.dataset.addFlavor);
    const plus = row?.querySelector('[data-buy-step="1"]');
    if (!row || !plus) return;
    event.preventDefault();
    setQuantity(row, quantityOf(row) + 1);
    row.scrollIntoView({ block: 'center', behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    plus.focus({ preventScroll: true });
  });

  // Clicking anywhere on a card selects its option, as the whole card is drawn as one.
  for (const card of cards) {
    card.addEventListener('click', (event) => {
      if (event.target.closest('select, a, button, label')) return;
      const input = card.querySelector('.radio-field__input');
      if (input && !input.checked) {
        input.checked = true;
        update({ speak: true });
      }
    });
  }

  // ?freq=<weeks> — marketing links name the interval, never a plan id.
  if (planSelect) {
    const freq = parseInt(new URL(location.href).searchParams.get('freq') || '', 10);
    if (freq > 0) {
      const option = [...planSelect.options].find((o) => o.dataset.interval === `${freq}-week`);
      if (option) {
        planSelect.value = option.value;
        const subscribe = root.querySelector('[data-buy-option="subscribe"] .radio-field__input');
        if (subscribe) subscribe.checked = true;
      }
    }
  }

  // ---- cart ----
  // Nothing in Liquid or the Ajax API exposes a discount DEFINITION, so the rate the
  // section rendered is a figure a merchant typed. The cart, though, reports the
  // discounts the engine actually applied: when one of them is the configured welcome
  // discount, its percentage is authoritative and the cards reprice. A cart with no
  // items, or none the discount applies to, reports nothing and the figure stands.
  async function syncWelcomeRate() {
    const title = (data.welcomeTitle || '').trim().toLowerCase();
    if (!title) return;
    try {
      const cart = await json(`${data.cartUrl}.js`);
      const applications = [...(cart.cart_level_discount_applications || []), ...(cart.discount_applications || [])];
      const match = applications.find(
        (a) => a && a.value_type === 'percentage' && String(a.title || '').trim().toLowerCase() === title
      );
      if (!match) return;
      const bps = Math.round(Number(match.value) * 1000) / 10;
      if (!Number.isFinite(bps) || bps < 0 || bps === data.welcomeBps) return;
      data.welcomeBps = bps;
      update();
    } catch {
      /* the rendered figure stands */
    }
  }

  async function refreshHeaderCart() {
    const current = document.querySelectorAll('.header-cart');
    const wrapper = current[0]?.closest('.shopify-section');
    if (!wrapper) return;
    const sectionId = wrapper.id.replace('shopify-section-', '');
    const response = await fetch(`${data.rootUrl}?sections=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const html = (await response.json())[sectionId];
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.header-cart');
    current.forEach((node, i) => {
      if (fresh[i]) node.replaceWith(fresh[i]);
    });
  }

  // Re-adding from the builder replaces the bundle: every line carrying an earlier
  // `_bundle_key` goes, nothing else does. One batch update; a per-line change if the
  // batch is refused. Never /cart/clear.js.
  async function removePreviousBundles() {
    const cart = await json(`${data.cartUrl}.js`);
    const stale = (cart.items || []).filter((item) => item.properties && item.properties._bundle_key);
    if (!stale.length) return;
    const updates = Object.fromEntries(stale.map((item) => [item.key, 0]));
    try {
      await json(`${data.cartUpdateUrl}.js`, { method: 'POST', body: JSON.stringify({ updates }) });
    } catch {
      for (const item of stale) await json(`${data.cartChangeUrl}.js`, { method: 'POST', body: JSON.stringify({ id: item.key, quantity: 0 }) });
    }
  }

  const buildItems = (option, planId, key, tier) =>
    chosen().map((c) => {
      const item = { id: c.variant.id, quantity: c.quantity, properties: { _bundle_key: key, _bundle_tier: tier } };
      if (option === 'subscribe' && planId) item.selling_plan = planId;
      return item;
    });

  // Plan ids are not stable across Recharge/Shopify syncs: re-read the product and
  // point every option at the plan with the same interval, refreshing allocations too.
  async function refreshPlans() {
    const product = await json(data.productJsonUrl);
    const plans = resolvePlans(product);
    if (planSelect) {
      for (const option of planSelect.options) {
        const fresh = planByKey(plans, option.dataset.interval);
        if (fresh) option.value = String(fresh.id);
      }
    }
    for (const v of product.variants || []) {
      const known = variants.get(String(v.id));
      if (!known) continue;
      known.price = v.price;
      known.available = v.available;
      known.plans = (v.selling_plan_allocations || []).map((a) => ({ id: a.selling_plan_id, price: a.price, compareAtPrice: a.compare_at_price || 0 }));
    }
  }

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!submit || submit.disabled) return;
    const packs = totalPacks();
    if (packs <= 0) return;
    const option = currentOption();
    const key = uuid();
    const tier = sizeLabel(packs);

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    try {
      // The rows ARE the bundle, so adding them replaces the last one. A card is one pack
      // and leaves the cart alone: a second add is a second pack, not a swap.
      if (!picks.length) await removePreviousBundles();
      let planId = option === 'subscribe' ? currentPlanId() : null;
      let items = buildItems(option, planId, key, tier);
      try {
        await json(`${data.cartAddUrl}.js`, { method: 'POST', body: JSON.stringify({ items }) });
      } catch (error) {
        if (!planId) throw error;
        await refreshPlans();
        planId = currentPlanId();
        if (!planId) throw error;
        items = buildItems(option, planId, key, tier);
        await json(`${data.cartAddUrl}.js`, { method: 'POST', body: JSON.stringify({ items }) });
      }

      const detail = { items, key, packs, option, planId };
      document.dispatchEvent(new CustomEvent('cart:added', { detail }));
      // Recharge owns the cart UI where it is installed; its own fetch patch has
      // already published cart:item:add by now, and asking again is a no-op if the
      // drawer is open. A theme drawer would take the moment by cancelling cart:open.
      // Either way the page stays where it is: the cart page is never used.
      openRechargeCart();
      document.dispatchEvent(new CustomEvent('cart:open', { detail, cancelable: true }));
      if (status) status.textContent = s.added;
      announce(s.added, { immediate: true });
      // The add succeeded whatever the header does; never turn this into an error.
      await refreshHeaderCart().catch(() => {});
    } catch {
      if (status) status.textContent = s.error;
      announce(s.error, { immediate: true });
    } finally {
      submit.removeAttribute('aria-busy');
      update();
    }
  });

  // ---- nutrition dialog ----
  //
  // Opening and the flavour swap only. Closing is the platform's: the dialog's close
  // control is a <form method="dialog">, and showModal() brings Esc, the focus trap and
  // the top layer with it. The trigger is found by its ARIA rather than a data hook —
  // aria-haspopup="dialog" plus aria-controls already say exactly this — so the
  // behaviour cannot drift from what assistive tech is told.
  for (const trigger of root.querySelectorAll('[aria-haspopup="dialog"][aria-controls]')) {
    const dialog = root.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
    if (!dialog || typeof dialog.showModal !== 'function') continue;

    // initBuy has no idempotence guard and the theme editor calls scan() again on
    // shopify:section:load, so without this the listeners stack up and the second
    // showModal() of a pair throws InvalidStateError on an already-open dialog.
    if (dialog.dataset.wired === 'true') continue;
    dialog.dataset.wired = 'true';

    trigger.addEventListener('click', () => {
      if (!dialog.open) dialog.showModal();
    });

    // Clicking the backdrop. The dialog's own box is the click target for everything
    // inside it, so a press whose coordinates fall outside that box is the backdrop —
    // which is also why this checks geometry rather than event.target, the reliable
    // version of a test that otherwise fires on any padding click.
    dialog.addEventListener('click', (event) => {
      const box = dialog.getBoundingClientRect();
      const outside =
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom;
      if (outside) dialog.close();
    });

    const sheets = [...dialog.querySelectorAll('[data-nutrition-sheet]')];
    const picker = dialog.querySelector('[data-nutrition-picker]');
    const current = dialog.querySelector('[data-nutrition-current]');
    const options = [...dialog.querySelectorAll('[data-nutrition-option]')];

    // No flavour names live here: the chosen row's own text becomes the summary's, so
    // the copy stays in the markup Liquid built from the product.
    for (const option of options) {
      option.addEventListener('click', () => {
        const wanted = option.dataset.nutritionOption;
        for (const sheet of sheets) sheet.hidden = sheet.dataset.nutritionSheet !== wanted;
        for (const other of options) {
          if (other === option) other.setAttribute('aria-current', 'true');
          else other.removeAttribute('aria-current');
        }
        const name = option.querySelector('.nutrition-picker__name');
        if (current && name) current.textContent = name.textContent;
        // The bar takes the flavour it is now showing.
        if (picker && option.dataset.flavor) picker.dataset.flavor = option.dataset.flavor;
        if (picker) picker.open = false;
        // The sheet changed under the shopper; start them at its top.
        const body = dialog.querySelector('.nutrition-modal__body');
        if (body) body.scrollTop = 0;
      });
    }
  }

  // ---- gallery ----
  const gallery = root.querySelector('[data-buy-gallery]');
  const slides = gallery ? [...gallery.querySelectorAll('[data-gallery-slide]')] : [];
  const thumbs = gallery ? [...gallery.querySelectorAll('[data-gallery-thumb]')] : [];
  const strip = gallery?.querySelector('[data-gallery-strip]');
  const stripPrev = gallery?.querySelector('[data-gallery-strip-prev]');
  const stripNext = gallery?.querySelector('[data-gallery-strip-next]');

  // SWIPER OWNS THE STAGE. assets/carousel.js mounts it on [data-carousel] and its
  // Navigation module drives the arrows and disables them at the ends, so nothing here
  // moves slides or touches the arrows — this only keeps the thumbnail strip in step.
  //
  // The instance is reached through el.swiper at CALL time, never held: buy.js loads
  // before carousel.js, so at init there is nothing to hold.
  const stage = gallery?.querySelector('[data-gallery-stage]');

  function markThumbs(index) {
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    revealThumb(index);
  }

  function showMedia(id) {
    if (!slides.length) return;
    let index = slides.findIndex((slide) => slide.dataset.gallerySlide === id);
    if (index < 0) index = 0;
    if (stage?.swiper) {
      // slideTo fires slideChange, and the listener below marks the thumbs — doing it
      // here as well would just do it twice.
      stage.swiper.slideTo(index);
      return;
    }
    // No Swiper: the strip still works as a plain picker. Slides after the first hold
    // their sources back until the carousel mounts (product-gallery.liquid), so a slide
    // picked before then is given them here.
    const img = slides[index].querySelector('img[data-srcset]');
    if (img) {
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
    }
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    markThumbs(index);
  }

  // Swiper reports through a DOM event rather than a handle, so this needs no instance
  // and no mount order. See the on.slideChange note in assets/carousel.js.
  stage?.addEventListener('carousel:change', (event) => markThumbs(event.detail.index));

  // Bring a thumbnail into view by scrolling the STRIP, never the page. scrollIntoView
  // here scrolled the document to the buy box on load, because showMedia runs once to
  // set the first slide and the section is well below the fold on a fresh page.
  function revealThumb(index) {
    const thumb = thumbs[index];
    if (!strip || !thumb) return;
    const t = thumb.getBoundingClientRect();
    const s = strip.getBoundingClientRect();
    if (t.left < s.left) strip.scrollBy({ left: t.left - s.left, behavior: 'smooth' });
    else if (t.right > s.right) strip.scrollBy({ left: t.right - s.right, behavior: 'smooth' });
  }

  // prev/next are wired by Swiper's Navigation module, which also owns their disabled
  // state. Binding them here too would step twice per click. The thumbnails are ours:
  // Swiper's Thumbs module would want its own carousel and would fight the strip's
  // scrolling and its scroll tabs, so a click just asks showMedia to move the stage.
  for (const thumb of thumbs) {
    thumb.addEventListener('click', () => showMedia(thumb.dataset.galleryThumb));
  }

  // WHETHER THE STRIP SCROLLS IS A COUNT, not a measurement. CSS decides how many tiles
  // go to a strip width — four on a phone, five from the tablet step — and publishes it
  // as --thumbs-per-view, so "are there more images than fit" is that number against the
  // number of tiles. Comparing widths instead is what put an arrow on a desktop strip
  // with nothing to scroll to (reported 2026-09-22): a fractional tile width leaves the
  // strip a pixel or two over its box, and a gutter would leave it more.
  //
  // Two things follow from the same fact: the tabs, and the gutter after the last tile —
  // which only a scrolling strip should pay width for, so it is switched on here rather
  // than being in the layout always. Each tab then hides at its own end.
  function syncStrip() {
    if (!strip) return;
    const perView = parseFloat(getComputedStyle(strip).getPropertyValue('--thumbs-per-view')) || 0;
    const count = strip.querySelectorAll('.product-gallery__thumb-item').length;
    const scrollable = perView > 0 && count > perView;
    strip.classList.toggle('has-gutter', scrollable);
    const max = strip.scrollWidth - strip.clientWidth;
    stripPrev?.classList.toggle('is-hidden', !scrollable || strip.scrollLeft <= 1);
    stripNext?.classList.toggle('is-hidden', !scrollable || strip.scrollLeft >= max - 1);
  }

  // TO THE NEXT TILE'S OWN EDGE, not by a tile's width. A tile's width is a gap short of
  // the distance between two tiles, so scrolling by it left every tile after the first a
  // little cropped, and the strip's mandatory snap then fought the smooth scroll to fix
  // it. Reading the tiles' own offsets means a click always lands a tile flush against
  // the edge, whatever the count per strip width is (figma-changes §7.98).
  function scrollStrip(direction) {
    if (!strip) return;
    const items = [...strip.querySelectorAll('.product-gallery__thumb-item')];
    if (!items.length) return;
    const base = items[0].offsetLeft;
    const offsets = items.map((item) => item.offsetLeft - base);
    const here = strip.scrollLeft;
    const target =
      direction > 0
        ? offsets.find((x) => x > here + 1)
        : [...offsets].reverse().find((x) => x < here - 1);
    const end = direction > 0 ? strip.scrollWidth : 0;
    strip.scrollTo({ left: target === undefined ? end : target, behavior: 'smooth' });
  }
  stripPrev?.addEventListener('click', () => scrollStrip(-1));
  stripNext?.addEventListener('click', () => scrollStrip(1));
  strip?.addEventListener('scroll', syncStrip, { passive: true });
  // Two answers change with the width — the tabs', because the tiles per strip width
  // change at --breakpoint-md and the strip itself resizes, and the delivery line's,
  // because its column does — so a rotation or a window drag has to ask again.
  window.addEventListener(
    'resize',
    () => {
      syncStrip();
      fitDescriptions();
    },
    { passive: true }
  );
  if (slides.length) {
    showMedia(slides[0].dataset.gallerySlide);
    syncStrip();
  }

  // Restored form state — the flavour cards, the purchase option, the plan — lands
  // after this script has run and before `pageshow`, without a change event, so the
  // figures are brought back in line with what the form shows.
  window.addEventListener('pageshow', () => {
    if (!root.isConnected) return;
    if (picks.length) syncPicks();
    update();
  });

  update();
  syncWelcomeRate();
}

const scan = (scope = document) => {
  for (const root of scope.querySelectorAll('[data-buy]')) initBuy(root);
};
document.addEventListener('shopify:section:load', (event) => scan(event.target));
scan();
